/**
 * Featured Carousel picker options — mirrors Recent Content tabs only:
 * Latest + Series rows from `template_components` (not stayuptodate /
 * platform highlight fallbacks).
 */
import {
  isCarouselLatestTab,
  isCarouselSeriesTab,
} from "../../../Utils/StayUpToDate/stayUpToDateComponentTabFilters";
import type {
  CarouselData,
  TemplateContentView,
} from "./types";

export type RecentContentPickOption = {
  value: string;
  /** Exact tab title as shown on Recent Content (pills / section heading). */
  tabTitle: string;
  /** Picker row label — same as tabTitle. */
  label: string;
  contentView: TemplateContentView;
  kind: "carousel";
  /** `template_components` row id. */
  componentId?: string;
  carousel?: CarouselData;
};

/** @deprecated platform picks are no longer offered for Featured Carousel. */
export const PLATFORM_PICK_PREFIX = "platform:";

export function platformPickValue(platformKey: string): string {
  return `${PLATFORM_PICK_PREFIX}${String(platformKey || "").trim()}`;
}

export function parsePlatformPickValue(value: string): string | null {
  const v = String(value || "").trim();
  if (!v.startsWith(PLATFORM_PICK_PREFIX)) return null;
  const key = v.slice(PLATFORM_PICK_PREFIX.length).trim();
  return key || null;
}

/** Latest + Series `template_components` carousels (same filters as ContentGrid). */
export function listRecentContentCarousels(
  carousels: CarouselData[] | null | undefined,
): RecentContentPickOption[] {
  const list = Array.isArray(carousels) ? carousels : [];
  const out: RecentContentPickOption[] = [];

  for (const c of list) {
    if (!isCarouselLatestTab(c)) continue;
    const slug = String(c.renderSlug || "").trim();
    if (!slug) continue;
    out.push({
      value: slug,
      tabTitle: c.title || slug,
      label: c.title || slug,
      contentView: "latest",
      kind: "carousel",
      componentId:
        String(c.stayComponentRowId || c.id || "").trim() || undefined,
      carousel: c,
    });
  }
  for (const c of list) {
    if (!isCarouselSeriesTab(c)) continue;
    const slug = String(c.renderSlug || "").trim();
    if (!slug) continue;
    const tabTitle = c.title || slug;
    out.push({
      value: slug,
      tabTitle,
      label: `Series · ${tabTitle}`,
      contentView: "series",
      kind: "carousel",
      componentId:
        String(c.stayComponentRowId || c.id || "").trim() || undefined,
      carousel: c,
    });
  }
  return out;
}

/**
 * Full Featured Carousel picker list — Recent Content Latest + Series only.
 * `highlights` is ignored (kept for call-site compatibility).
 */
export function listRecentContentPicks(
  carousels: CarouselData[] | null | undefined,
  _highlights?: unknown,
): RecentContentPickOption[] {
  return listRecentContentCarousels(carousels);
}

/** Custom content/products tabs — hand-picked `post_ids` (not Latest/Series feeds). */
export function isHandPickCarousel(c: CarouselData): boolean {
  const type = String(c.componentType || "").toLowerCase().trim();
  if (type !== "content" && type !== "shoppable" && type !== "links") {
    return false;
  }
  // CUSTOM order is set at Add — keep the picker even if every post was later removed.
  const order = String(c.contentOrder || "").toUpperCase();
  if (order === "CUSTOM") return true;
  return Boolean(String(c.postIds || c.customOrder || "").trim());
}

/** Match a carousel by its Stay row id — stable across title renames. */
export function findCarouselByComponentId(
  carousels: CarouselData[] | null | undefined,
  componentId: string,
): CarouselData | null {
  const needle = String(componentId || "").trim();
  if (!needle) return null;
  const list = Array.isArray(carousels) ? carousels : [];
  return (
    list.find(
      (c) => String(c.stayComponentRowId || "").trim() === needle,
    ) || null
  );
}

/** Match any carousel row by slug, id, row id, or title (case-insensitive). */
export function findCarouselBySlug(
  carousels: CarouselData[] | null | undefined,
  slug: string,
): CarouselData | null {
  const needle = String(slug || "").trim();
  if (!needle || needle === "auto") return null;
  const needleLc = needle.toLowerCase();
  const list = Array.isArray(carousels) ? carousels : [];
  return (
    list.find(
      (c) => String(c.stayComponentRowId || "").trim().toLowerCase() === needleLc,
    ) ||
    list.find(
      (c) => String(c.renderSlug || "").trim().toLowerCase() === needleLc,
    ) ||
    list.find((c) => String(c.id || "").trim().toLowerCase() === needleLc) ||
    null
  );
}

function handPickToOption(c: CarouselData): RecentContentPickOption {
  const slug = String(c.renderSlug || c.id || c.title || "").trim();
  return {
    value: slug,
    tabTitle: c.title || slug,
    label: c.title || slug,
    contentView: "latest",
    kind: "carousel",
    componentId: c.stayComponentRowId,
    carousel: c,
  };
}

/** Select options including Auto (carousel-collection) for section tweaks. */
export function buildFeaturedCarouselSelectOptions(
  carousels: CarouselData[] | null | undefined,
  _highlights?: unknown,
): { value: string; label: string }[] {
  return [
    { value: "auto", label: "Auto (collection)" },
    ...listRecentContentCarousels(carousels).map(({ value, tabTitle, label }) => ({
      value,
      label: label || tabTitle,
    })),
  ];
}

export function findRecentContentPick(
  carousels: CarouselData[] | null | undefined,
  slug: string,
  _highlights?: unknown,
): RecentContentPickOption | null {
  const needle = String(slug || "").trim();
  if (!needle || needle === "auto") return null;
  const needleLc = needle.toLowerCase();
  const list = listRecentContentCarousels(carousels);
  const byValue = list.find((o) => o.value.toLowerCase() === needleLc);
  if (byValue) return byValue;
  // Older Add flow saved type as slug (`content-series`) when route/unique_id
  // were missing — recover by title / first Series tab.
  if (needleLc === "content-series" || needleLc === "query-content") {
    return list.find((o) => o.contentView === "series") ?? null;
  }
  // Legacy slug stored as carousel id — never match tabTitle (duplicate titles allowed).
  const byCarouselId = list.find(
    (o) => String(o.carousel?.id || "").toLowerCase() === needleLc,
  );
  if (byCarouselId) return byCarouselId;
  const handPick = findCarouselBySlug(carousels, needle);
  if (handPick && isHandPickCarousel(handPick)) {
    return handPickToOption(handPick);
  }
  return null;
}

/** @deprecated use findRecentContentPick */
export function findRecentContentCarousel(
  carousels: CarouselData[] | null | undefined,
  slug: string,
): RecentContentPickOption | null {
  return findRecentContentPick(carousels, slug);
}

/**
 * Build pick options from raw `template_components` rows —
 * same Latest/Series type filters as Recent Content.
 */
export function listRecentContentPicksFromComponents(
  components: Array<{
    id: string;
    name?: string;
    type?: string;
    display_name?: string;
    status?: string;
    status_expiry?: string;
    unique_id?: string;
    route_name?: string;
  }>,
): RecentContentPickOption[] {
  const out: RecentContentPickOption[] = [];
  for (const c of components) {
    const st = String(c.status ?? "").toLowerCase().trim();
    const exp = String(c.status_expiry ?? "").toLowerCase().trim();
    if (
      st === "deleted" ||
      exp === "deleted" ||
      st === "hidden" ||
      exp === "hidden"
    ) {
      continue;
    }
    const shape = {
      componentType: c.type,
      componentDisplayName: c.display_name,
      name: c.name,
      title: c.name,
    };
    const isLatest = isCarouselLatestTab(shape);
    const isSeries = isCarouselSeriesTab(shape);
    if (!isLatest && !isSeries) continue;
    const slug = String(
      (c.route_name && String(c.route_name).trim()) ||
        (c.unique_id && String(c.unique_id).trim()) ||
        (c.name && String(c.name).trim()) ||
        "",
    ).trim();
    if (!slug) continue;
    const title =
      String(c.name ?? "").trim() ||
      String(c.display_name ?? "").trim() ||
      slug;
    out.push({
      value: slug,
      tabTitle: title,
      label: isSeries ? `Series · ${title}` : title,
      contentView: isSeries ? "series" : "latest",
      kind: "carousel",
      componentId: String(c.id).trim() || undefined,
    });
  }
  return out;
}
